import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtMoney, fmtErr, photoSrc } from "../../api";
import Layout from "../../components/Layout";
import { QuoteView } from "../../components/QuoteView";
import { toast } from "sonner";

export default function OwnerJobDetail() {
  const { id } = useParams();
  const [j, setJ] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const nav = useNavigate();

  const load = () => api.get(`/jobs/${id}`).then((r) => setJ(r.data)).catch((e) => toast.error(fmtErr(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when the route id changes; load is stable
  useEffect(() => { load(); }, [id]);

  if (!j) return <Layout title="Project quote"><p className="text-muted-foreground">Loading…</p></Layout>;

  const decide = async (approve) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${id}/decide`, { approve });
      toast.success(approve ? "Approved — your operator will schedule the work" : "Declined");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  return (
    <Layout title={j.title}>
      <div className="space-y-6">
        <div className="card-flat p-6">
          <p className="font-heading text-xl font-bold" data-testid="owner-job-title">{j.title}</p>
          <p className="text-sm text-muted-foreground">{j.property_name}</p>
          {j.description && <p className="mt-2 text-sm">{j.description}</p>}
          {j.photos?.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{j.photos.map((p, i) => <img key={`${i}-${p.length}`} src={photoSrc(p)} alt="" className="h-20 w-20 rounded-md object-cover" />)}</div>}
        </div>

        <div className="card-flat divide-y divide-border" data-testid="owner-job-lines">
          {j.line_items.map((l, i) => (
            <div key={`${l.description}-${i}`} className="flex items-center justify-between gap-3 p-4">
              <div>
                <p className="text-sm font-semibold">{l.description}</p>
                <p className="text-xs text-muted-foreground capitalize">{l.kind} · qty {l.qty} × {fmtMoney(l.unit_price)}</p>
              </div>
              <span className="font-bold">{fmtMoney(l.amount)}</span>
            </div>
          ))}
        </div>

        <div className="card-flat space-y-1 p-4 text-sm" data-testid="owner-job-totals">
          <p className="flex justify-between"><span>Subtotal</span><span className="font-bold">{fmtMoney(j.subtotal)}</span></p>
          {j.tax > 0 && <p className="flex justify-between"><span>{j.tax_label || "HST"}</span><span className="font-bold">{fmtMoney(j.tax)}</span></p>}
          <p className="flex justify-between text-base"><span>Total</span><span className="font-heading font-bold text-primary" data-testid="owner-job-total">{fmtMoney(j.total)}</span></p>
          {j.deposit_percent > 0 && (
            <p className="mt-2 rounded-md bg-muted p-3 text-xs" data-testid="owner-job-deposit">
              A deposit of <strong>{fmtMoney(j.deposit_total ?? j.deposit_amount)}</strong> ({j.deposit_percent}%) is invoiced when you approve. The balance is billed when the work is complete.
            </p>
          )}
        </div>

        {j.status === "quoted" && (
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="owner-job-approve" onClick={() => decide(true)} disabled={busy} className="h-14 rounded-md bg-primary text-lg font-bold text-primary-foreground disabled:opacity-40">Approve</button>
            <button data-testid="owner-job-decline" onClick={() => decide(false)} disabled={busy} className="h-14 rounded-md border border-destructive text-lg font-bold text-destructive disabled:opacity-40">Decline</button>
          </div>
        )}
        {j.status !== "quoted" && (
          <div className="card-flat p-4 text-center" data-testid="owner-job-decided">
            <p className="font-heading text-lg font-bold capitalize">{j.status}</p>
            {j.status === "approved" && <p className="text-sm text-muted-foreground">Your operator will be in touch to schedule.</p>}
            {j.status === "lost" && <p className="text-sm text-muted-foreground">This quote was declined.</p>}
          </div>
        )}
        {j.status !== "draft" && (
          <button data-testid="owner-view-quote" onClick={() => setShowQuote(true)} className="h-12 w-full rounded-md border border-border text-sm font-bold">View quote document</button>
        )}
        <button data-testid="owner-job-back" onClick={() => nav("/owner/jobs")} className="h-12 w-full rounded-md border border-border text-sm font-bold">Back to quotes</button>
        {showQuote && <QuoteView jobId={id} onClose={() => setShowQuote(false)} testid="owner-quote-view" />}
      </div>
    </Layout>
  );
}
